<template>
  <div class="image-picker mb-5">
    <div
      class="input-slot image-picker mb-5"
      data-cy="image-picker-input"
      :label="label"
      :hint="hint"
      :persistent-hint="true"
    >
      <v-col class="container">
        <!-- <v-img
          v-if="imageId"
          :src="imageUrl"
          :lazy-src="showLazy ? 'https://picsum.photos/128/?blur=5' : undefined"
          contain
          min-height="200"
          max-height="250"
        >
          <div v-if="!imageId" class="no-image-placeholder">
            <svg
              id="Flat"
              fill="#ffffff"
              viewBox="0 0 256 256"
              xmlns="http://www.w3.org/2000/svg"
              stroke="#ffffff"
              stroke-width="0.00256"
              transform="matrix(-1, 0, 0, 1, 0, 0)"
            >
              <g id="SVGRepo_bgCarrier" stroke-width="0" />
              <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round" />
              <g id="SVGRepo_iconCarrier">
                <path
                  d="M208.00049,31.99963h-160a16.01833,16.01833,0,0,0-16,16V175.97369l-.001.0307.001,31.99524a16.01833,16.01833,0,0,0,16,16h160a16.01833,16.01833,0,0,0,16-16v-160A16.01834,16.01834,0,0,0,208.00049,31.99963Zm-28.68653,80a16.019,16.019,0,0,0-22.62792,0l-44.68555,44.68653L91.314,135.99963a16.02161,16.02161,0,0,0-22.62792,0L48.00049,156.68457V47.99963h160l.00586,92.6922ZM111.99951,92a12,12,0,1,1-12-12A12,12,0,0,1,111.99951,92Z"
                />
              </g>
            </svg>
          </div>
        </v-img> -->

        <v-row class="image-actions mt-5 pb-2 ml-0 mr-0" justify="center">
          <v-btn
            id="imagePickerRandomButton"
            :aria-label="t('aria.label.randomizeImage')"
            text
            class="image-action-btn"
            :ripple="false"
            dense
            :disabled="!images || images.length === 0"
            data-cy="image-picker-btn-random"
            width="100%"
            @click="random"
          >
            {{ t('components.imagePicker.random') }}
          </v-btn>
          <v-dialog
            v-model="dialog"
            data-cy="image-picker-dialog"
            transition="slide-y-transition"
            scrollable
            max-width="800px"
          >
            <template #activator="{ props }">
              <v-btn
                id="imagePickerChoiceButton"
                :aria-label="t('aria.label.selectImage')"
                text
                class="image-action-btn"
                :ripple="false"
                :disabled="!images || images.length === 0"
                data-cy="image-picker-btn-pick"
                v-bind="props"
              >
                {{ t('components.imagePicker.pick') }}
              </v-btn>
            </template>
            <image-picker-view
              v-model="imageId"
              data-cy="image-picker-view-close-btn"
              :images="images"
              :categories="categories"
              @close="dialog = false"
            />
          </v-dialog>
        </v-row>
        <v-progress-linear indeterminate :active="loading" />
      </v-col>
    </div>
  </div>
</template>

<script lang="ts">
import ImagePickerView from '@/views/image/ImagePickerView.vue';
import { defineComponent, ref, computed, watch } from 'vue';
import { useLabel } from '@/composables/useLabel';
import { DonationImage } from '@/interfaces/donation/donationImage';
import matomo from '@/services/matomoService';

export default defineComponent({
  components: { ImagePickerView },
  props: {
    modelValue: {
      type: Number,
      default: null,
    },
    label: {
      type: String,
      default: null,
    },
    hint: {
      type: String,
      default: null,
    },
    images: {
      type: Array,
      default: null,
    },
    categories: {
      type: Array,
      default: null,
    },
    showLazy: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const { t } = useLabel();
    const dialog = ref(false);
    const loading = ref(false);
    const imageId = ref(props.modelValue);

    const imageUrl = computed(() => {
      const images = props.images as DonationImage[] | null;
      const image = images?.find((i) => i.id === imageId.value);
      return image?.url;
    });

    watch(
      imageId,
      (val) => {
        // propagate model changes to parent via v-model binding
        emit('update:modelValue', val);
      },
      { immediate: true }
    );

    const random = () => {
      const images = props.images as DonationImage[];
      if (!images || images.length === 0) return;
      loading.value = true;
      const newId = images[Math.floor(Math.random() * images.length)].id;
      if (newId != imageId.value) {
        imageId.value = newId;
        matomo.trackEvent(
          'certificate',
          'image_selected_random',
          'donations_certificate_image_selected_random'
        );
      } else {
        random();
      }
      loading.value = false;
    };

    return {
      t,
      dialog,
      loading,
      imageId,
      imageUrl,
      random,
    };
  },
});
</script>
<style>
.image-picker .v-input__slot,
.image-picker .input-slot {
  flex-direction: column;
  align-items: start;
}
</style>
<style scoped lang="scss">
#imagePickerRandomButton,
#imagePickerChoiceButton {
  text-transform: none;
  text-decoration: underline;
  font-size: 14px;
}

.v-image {
  border-radius: 12px;
}

.no-image-placeholder {
  background: #dce2e5;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 10;
}

.no-image-placeholder svg {
  width: 128px;
  height: 128px;
}

.container {
  display: grid;
  grid-template-columns: 1fr;
  min-width: 100%;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
}

.container > * {
  flex: 1;
  min-width: 50%;
}

.container .image-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  padding: 0;
  align-items: center !important;
  justify-content: center !important;
  margin: 0;
  margin-top: 16px;
  width: 100%;
}

.image-action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: transparent !important;
  box-shadow: none !important;
  text-transform: uppercase !important;
  font-weight: 700 !important;
  font-size: 17px !important;
  color: #374151 !important;
  letter-spacing: normal !important;
  text-decoration: none !important;
  min-width: unset !important;
  width: 100% !important;
}

.image-action-btn .v-btn__content {
  text-decoration: none !important;
  font-weight: 800 !important;
}

.container .v-image {
  width: 50%;
  border-radius: 8px;
}

.image {
  background-color: grey;
  height: 200px;
  width: 100%;
}

@media (max-width: 1080px) {
  .image-picker-container {
    display: flex;
    flex-direction: column !important;

    .v-image {
      min-width: 100%;
    }

    .image-picker {
      .v-messages__wrapper {
        display: none !important;
      }
    }

    .image-actions {
      padding: 0;
      margin-top: 16px;
    }

    #imagePickerRandomButton,
    #imagePickerChoiceButton {
      padding: 12px !important;
    }
  }
}
/* Kill hover/focus wash and force full-emphasis text */
.link-btn.v-btn--text {
  background: transparent !important;
  box-shadow: none !important;
}
.link-btn .v-btn__content {
  opacity: 1 !important;
} /* override Vuetify’s medium-emphasis */
.link-btn::before {
  opacity: 0 !important;
} /* remove hover/focus overlay */
.link-btn:focus {
  outline: none !important;
} /* avoid focus ring */
</style>
